import React, { useState, useEffect, Fragment } from 'react';
import ReactModal from 'react-modal';
import secureLocalStorage from 'react-secure-storage';
import { Button } from '../../../theme/button';
import styles from './Export.module.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { useAuth } from '../../../contexts/AuthContext';
import autoTable from 'jspdf-autotable';
import { ReactComponent as Close } from '../../../assets/icons/svg/close-box-outline.svg';
import { ReactComponent as ExcelIcon } from '../../../assets/icons/svg/file-excel-outline.svg';
import { ReactComponent as PdfIcon } from '../../../assets/icons/svg/file-pdf-box.svg';

ReactModal.setAppElement('#root');

export default function ExportPage() {
  const [name, setName] = useState(null);
  const [email, setEmail] = useState(null);
  const [logbooks, setLogbooks] = useState([]);
  const [selectedLogbook, setSelectedLogbook] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const { getValidToken } = useAuth();

  useEffect(() => {
    userDataOnPageLoad();
  }, []);

  const userDataOnPageLoad = async () => {
    try {
      const user = localStorage.getItem('user');
      const userObject = JSON.parse(user);
      setEmail(userObject?.email);
      setName(userObject?.name);

      if (userObject?.email) {
        const result = await queryLogbooksByUserEmail(userObject.email);
        if (Array.isArray(result)) setLogbooks(result);
        else setLogbooks([]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const queryLogbooksByUserEmail = async () => {
    const API_URL =
      'https://ngyk2k01bl.execute-api.us-east-1.amazonaws.com/prod/getLogbooks';
    const validToken = await getValidToken();
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${validToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API fetch error:', error);
      return [];
    }
  };

  const openModal = (logbook) => {
    setSelectedLogbook(logbook);
    setModalVisible(true);
  };

  const closeModal = () => {
    setSelectedLogbook(null);
    setModalVisible(false);
  };

  // ----- Export Helpers -----

const arrayToObjects = (arr) => {

  if (!Array.isArray(arr) || arr.length === 0) {
    return [];
  }

  const header = arr[0];

  const rows = arr.slice(1); // all remaining rows

  const objects = rows.map((row, rowIndex) => {
    const obj = {};
    header.forEach((key, colIndex) => {
      obj[key] = row[colIndex] !== undefined && row[colIndex] !== null ? row[colIndex] : ''; // keep "" as empty cell
    });
    return obj;
  });
  return objects;
};



const handleExportToExcel = (data, fileName) => {

  // If your data is a JSON string (like in RN), parse it
  const twoDArray = typeof data === 'string' ? JSON.parse(data) : data;

  // Create a workbook
  const wb = XLSX.utils.book_new();

  // Create worksheet from 2D array
  const ws = XLSX.utils.aoa_to_sheet(twoDArray);

  // Optional: set some column widths
  ws['!cols'] = twoDArray[0].map(() => ({ wch: 20 })); // all columns 20 chars wide

  // Append worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Logbook');

  // Write file directly to download
  XLSX.writeFile(wb, `${fileName}.xlsx`);

};

const generateSplitTableHTML = (title, columns, rows, maxColumns = 6) => {
  const splitTables = [];

  for (let i = 0; i < columns.length; i += maxColumns) {
    const colSlice = columns.slice(i, i + maxColumns);

    let tableHTML = '<table><thead><tr>';
    for (const col of colSlice) {
      tableHTML += `<th>${col}</th>`;
    }
    tableHTML += '</tr></thead><tbody>';

    for (const row of rows) {
      tableHTML += '<tr>';
      for (let j = i; j < i + maxColumns; j++) {
        tableHTML += `<td>${row[j] ?? ''}</td>`;
      }
      tableHTML += '</tr>';
    }

    tableHTML += '</tbody></table><br/>';
    splitTables.push(tableHTML);
  }

  return `
    <html>
      <head>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            table-layout: fixed;
            margin-bottom: 40px;
          }
          th, td {
            border: 1px solid #444;
            padding: 6px;
            text-align: left;
            white-space: normal;
            word-wrap: break-word;
            overflow-wrap: break-word;
            width: 150px;
          }
          th {
            background-color: #f2f2f2;
          }
          h1 {
            text-align: center;
            margin-bottom: 20px;
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        ${splitTables.join('\n')}
      </body>
    </html>
  `;
};

const handleExportToPDF = async (data, title = 'Logbook') => {
  // Parse JSON string if needed
  const twoDArray = typeof data === 'string' ? JSON.parse(data) : data;
  if (!twoDArray || twoDArray.length === 0) return;

  const columns = twoDArray[0];
  const rows = twoDArray.slice(1);

  const html = generateSplitTableHTML(title, columns, rows, 6);

  // Use jsPDF html method
  const doc = new jsPDF('l', 'pt', 'a4'); // landscape A4
  await doc.html(html, {
    callback: (pdf) => {
      pdf.save(`${title}.pdf`);
    },
    x: 10,
    y: 10,
    html2canvas: { scale: 0.8 }, // makes it look cleaner
  });
};
  

  return (
    <div>
      <div className={styles.container}>
        <h1 className={styles.headerText}>Export</h1>
        <div className={styles.scrollContainer}>
          {logbooks.length === 0 ? (
            <div style={{ textAlign: 'center', marginTop: 50 }}>
              <p style={{ fontSize: 16, color: 'gray' }}>No logbooks available for export.</p>
            </div>
          ) : (
            logbooks.map((logbook, index) => (
              <div key={index} className={styles.logbookEntryContainer}>
                <div className={styles.textContainer}>
                  <p className={styles.logbookName}>{logbook.Name}</p>
                  <div className={styles.rowCountColumnCountContainer}>
                    <span className={styles.columnCountText}>Columns: {logbook.Columns} - </span>
                    <span className={styles.rowCountText}>Rows: {logbook.Rows}</span>
                  </div>
                </div>
                <div className={styles.buttonContainer}>
                  <button
                    onClick={() => openModal(logbook)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <img
                      src={require('../../../assets/icons/png/table-arrow-right.png')}
                      alt="Open"
                      style={{ width: 30, height: 30 }}
                    />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
     <ReactModal
        isOpen={isModalVisible}
        onRequestClose={closeModal}
        contentLabel="Export Logbook Modal"
        className={styles.modalContent}
        overlayClassName={styles.modalOverlay}
        >
        {/* Top-right close button */}
            <div className={styles.modalHeaderRow}>
                <h2 className={styles.modalHeader}>Export Logbook</h2>
                <button
                    onClick={closeModal}
                    className={styles.closeButton}
                    aria-label="Close"
                >
                    <Close style={{ width: 30, height: 30, fill: 'red' }} />
                </button>
            </div>

            <div className={styles.modalDetails}>
                <p><strong>Name:</strong> {selectedLogbook?.Name}</p>
                <p><strong>Columns:</strong> {selectedLogbook?.Columns}</p>
                <p><strong>Rows:</strong> {selectedLogbook?.Rows}</p>
                <p><strong>Date Created:</strong> {selectedLogbook?.DateCreated}</p>
                <p><strong>Date Last Accessed:</strong> {selectedLogbook?.DateLastAccessed}</p>
            </div>

            <div className={styles.modalActions}>
                <button
                    onClick={() => handleExportToExcel(selectedLogbook?.Data, selectedLogbook?.Name)}
                    className={styles.exportButton}
                    aria-label="Export to Excel"
                >
                    <ExcelIcon style={{ width: 48, height: 48, fill: '#107C41', marginBottom: 6 }} />
                    <span>Excel</span>
                </button>

                <button
                    onClick={() => handleExportToPDF(selectedLogbook?.Data, selectedLogbook?.Name)}
                    className={styles.exportButton}
                    aria-label="Export to PDF"
                >
                    <PdfIcon style={{ width: 48, height: 48, fill: '#D32F2F', marginBottom: 6 }} />
                    <span>PDF</span>
                </button>
            </div>
        </ReactModal>

    </div>
  );
}
